<script lang="ts">
	import { ChevronLeftIcon } from '../../../../assets/icons/ChevronLeftIcon';
	import { ChevronRightIcon } from '../../../../assets/icons/ChevronRightIcon';
	import { Txt } from '../../../../components/Txt';
	import { extractNonNull } from '../../../../utils/extractNonNull/extractNonNull';
	import { getImageUrl } from '../../../../utils/getImageUrl';
	import type { ArticleCarouselType, SafeCarouselImage } from '../../types';

	interface Props {
		item: ArticleCarouselType;
	}

	let { item }: Props = $props();

	let safeItem = $derived(extractNonNull(item, ['description', 'carousel_images'], {}));

	let images = $derived(
		safeItem?.carousel_images.reduce<SafeCarouselImage[]>((acc, carouselImage) => {
			const articleImage = carouselImage?.Article_Image_id;

			if (!articleImage?.image?.filename_disk) {
				return acc;
			}

			return [
				...acc,
				{
					filename_disk: getImageUrl(articleImage.image.filename_disk),
					description: articleImage.image.description,
					layout: articleImage.layout,
					legend: articleImage.legend
				}
			];
		}, []) ?? []
	);

	let currentIndex = $state(0);
	let startX = 0;

	const next = () => {
		currentIndex = (currentIndex + 1) % images.length;
	};

	const prev = () => {
		currentIndex = (currentIndex - 1 + images.length) % images.length;
	};

	const handleTouchStart = (event: TouchEvent) => {
		startX = event.touches[0].clientX;
	};

	const handleTouchEnd = (event: TouchEvent) => {
		const endX = event.changedTouches[0].clientX;
		const diffX = endX - startX;

		if (diffX < -50) {
			next();
		} else if (diffX > 50) {
			prev();
		}
	};
</script>

{#if safeItem}
	<div class="relative h-[15rem] w-full overflow-hidden md:h-[20rem] lg:h-[25rem]">
		<div
			class="absolute inset-0 flex transition-transform duration-500 ease-in-out"
			style={`transform: translateX(-${currentIndex * 100}%)`}
			ontouchstart={handleTouchStart}
			ontouchend={handleTouchEnd}
			role="region"
		>
			{#each images as { filename_disk, layout, legend }, index}
				<div class="w-full flex-none">
					{#if layout === 'cover'}
						<img
							class="h-full w-full object-cover"
							src={filename_disk}
							alt={legend || `${safeItem.description}-${index}`}
							data-cy="blog-article-carousel-image"
						/>
					{:else}
						<div class="bg-gray-75 flex h-full w-full items-center justify-center">
							<img
								src={filename_disk}
								alt={legend || `${safeItem.description}-${index}`}
								data-cy="blog-article-carousel-image"
								class="max-h-full max-w-full"
							/>
						</div>
					{/if}
				</div>
			{/each}
		</div>

		<div class="absolute inset-y-0 left-0 flex items-center">
			<button
				class="ml-2 rounded bg-gray-500 p-1 hover:bg-gray-600"
				onclick={prev}
				aria-label="See previous image"
				data-cy="blog-article-carousel-left-button"
				><ChevronLeftIcon color="gray-0" />
			</button>
		</div>
		<div class="absolute inset-y-0 right-0 flex items-center">
			<button
				class="mr-2 rounded bg-gray-500 p-1 hover:bg-gray-600"
				onclick={next}
				data-cy="blog-article-carousel-right-button"
				aria-label="See next image"
			>
				<ChevronRightIcon color="gray-0" />
			</button>
		</div>
	</div>
	{#if item.captions_enabled && images[currentIndex]?.legend}
		<Txt variant="label4" class="mt-1 text-gray-500" data-cy="blog-article-carousel-description">
			{images[currentIndex]?.legend}
		</Txt>
	{/if}
{/if}
